/**
 * Cumple — Progress helpers (Sprint 11).
 *
 * Funciones puras para calcular cuántas preguntas se han respondido
 * en una sección, plantilla o conjunto de plantillas (assessment).
 *
 * Se llaman desde el navegador en tiempo real mientras el auditor
 * captura. NO requieren red ni Prisma. Pueden testearse de forma
 * aislada.
 */
import type { Question, Section, SurveyTemplate } from './templateSchema.js';

/**
 * Una respuesta cuenta como "respondida" si:
 * - No es undefined ni null
 * - Si es string, tiene al menos un caracter no-whitespace
 * - Si es array u objeto, tiene al menos un elemento/key
 * - Cualquier otro valor (number, boolean) cuenta como respondido
 */
export function isAnswered(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}

export interface ProgressCounts {
  answered: number;
  total: number;
  required: number;
  requiredAnswered: number;
}

export function sectionProgress(
  section: Section,
  responses: Record<string, unknown>,
): ProgressCounts {
  let answered = 0;
  let required = 0;
  let requiredAnswered = 0;
  for (const q of section.questions) {
    const a = isAnswered(responses[q.id]);
    if (a) answered++;
    if (q.required) {
      required++;
      if (a) requiredAnswered++;
    }
  }
  return { answered, total: section.questions.length, required, requiredAnswered };
}

export function templateProgress(
  template: SurveyTemplate,
  responses: Record<string, unknown>,
): ProgressCounts {
  let answered = 0;
  let total = 0;
  let required = 0;
  let requiredAnswered = 0;
  for (const s of template.sections) {
    const p = sectionProgress(s, responses);
    answered += p.answered;
    total += p.total;
    required += p.required;
    requiredAnswered += p.requiredAnswered;
  }
  return { answered, total, required, requiredAnswered };
}

/**
 * Combina N plantillas + sus respuestas en un solo progress global
 * (lo que ve el auditor en la sticky bottom bar del Assessment).
 */
export interface AssessmentProgressInput {
  template: SurveyTemplate;
  responses: Record<string, unknown>;
}

export function assessmentProgress(items: AssessmentProgressInput[]): ProgressCounts {
  let answered = 0;
  let total = 0;
  let required = 0;
  let requiredAnswered = 0;
  for (const it of items) {
    const p = templateProgress(it.template, it.responses);
    answered += p.answered;
    total += p.total;
    required += p.required;
    requiredAnswered += p.requiredAnswered;
  }
  return { answered, total, required, requiredAnswered };
}

/**
 * Porcentaje 0-100, redondeado a entero. Devuelve 0 si total es 0
 * (evita NaN).
 */
export function progressPercent(p: { answered: number; total: number }): number {
  if (p.total === 0) return 0;
  return Math.round((p.answered / p.total) * 100);
}

/**
 * Smart resume — devuelve la primera sección + pregunta no respondida.
 * Se usa al abrir un assessment para saltar directo donde el auditor lo dejó.
 * Si todas están respondidas, devuelve null.
 */
export interface ResumePosition {
  sectionIndex: number;
  questionId: string;
}

export function firstUnansweredInTemplate(
  template: SurveyTemplate,
  responses: Record<string, unknown>,
): ResumePosition | null {
  for (let s = 0; s < template.sections.length; s++) {
    const section = template.sections[s];
    if (!section) continue;
    for (const q of section.questions) {
      if (!isAnswered(responses[q.id])) {
        return { sectionIndex: s, questionId: q.id };
      }
    }
  }
  return null;
}

/**
 * Estado visual de una pregunta para los indicadores de la QuestionCard.
 */
export type QuestionState = 'answered' | 'required-pending' | 'pending';

export function questionState(q: Question, value: unknown): QuestionState {
  if (isAnswered(value)) return 'answered';
  if (q.required) return 'required-pending';
  return 'pending';
}
